import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { useScope } from "@/hooks/use-scope";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  CalendarClock, ChevronDown, ChevronRight, Loader2, Plus, ShieldAlert, Trash2, Users,
} from "lucide-react";
import { format, formatDistanceToNow, isAfter, isBefore } from "date-fns";
import {
  deleteRotation,
  deleteShift,
  listRotations,
  listTenantTeammates,
  upsertRotation,
  upsertShift,
} from "@/lib/on-call.functions";

export const Route = createFileRoute("/_authenticated/app/settings/on-call")({
  head: () => ({
    meta: [
      { title: "On-call rotations — CairnOS" },
      {
        name: "description",
        content: "Manage on-call rotations, escalation windows, and shift assignments for incident response.",
      },
    ],
  }),
  component: OnCallPage,
});

type Shift = {
  id: string;
  rotation_id: string;
  user_id: string;
  role: "primary" | "secondary" | "lead" | string;
  starts_at: string;
  ends_at: string;
  contact_email: string | null;
  contact_phone: string | null;
  notes: string | null;
};

function shiftStatus(s: Shift): "active" | "upcoming" | "past" {
  const now = new Date();
  if (isAfter(new Date(s.starts_at), now)) return "upcoming";
  if (isBefore(new Date(s.ends_at), now)) return "past";
  return "active";
}

function OnCallPage() {
  const { tenantId } = useScope();
  const qc = useQueryClient();

  const listFn = useServerFn(listRotations);
  const teamFn = useServerFn(listTenantTeammates);
  const upsertRotFn = useServerFn(upsertRotation);
  const delRotFn = useServerFn(deleteRotation);
  const upsertShiftFn = useServerFn(upsertShift);
  const delShiftFn = useServerFn(deleteShift);

  const rotQ = useQuery({
    queryKey: ["on-call", tenantId],
    enabled: !!tenantId,
    queryFn: () => listFn({ data: { tenantId: tenantId! } }),
  });

  const teamQ = useQuery({
    queryKey: ["on-call-team", tenantId],
    enabled: !!tenantId,
    queryFn: () => teamFn({ data: { tenantId: tenantId! } }),
  });

  const rotations = rotQ.data?.rotations ?? [];
  const allShifts = (rotQ.data?.shifts ?? []) as Shift[];
  const teammates = teamQ.data?.teammates ?? [];

  const shiftsByRotation = useMemo(() => {
    const m = new Map<string, Shift[]>();
    for (const s of allShifts) {
      if (!m.has(s.rotation_id)) m.set(s.rotation_id, []);
      m.get(s.rotation_id)!.push(s);
    }
    return m;
  }, [allShifts]);

  const teammateLabel = (id: string) => {
    const t = teammates.find((x) => x.id === id);
    return t?.name || id.slice(0, 8);
  };

  // Rotation dialog
  const [rotOpen, setRotOpen] = useState(false);
  const [editingRot, setEditingRot] = useState<any | null>(null);
  const [rotForm, setRotForm] = useState({
    name: "",
    description: "",
    ackWindowMinutes: 15,
    escalationMinutes: 30,
    isActive: true,
  });
  const openNewRot = () => {
    setEditingRot(null);
    setRotForm({ name: "", description: "", ackWindowMinutes: 15, escalationMinutes: 30, isActive: true });
    setRotOpen(true);
  };
  const openEditRot = (r: any) => {
    setEditingRot(r);
    setRotForm({
      name: r.name,
      description: r.description ?? "",
      ackWindowMinutes: r.ack_window_minutes,
      escalationMinutes: r.escalation_minutes,
      isActive: r.is_active,
    });
    setRotOpen(true);
  };

  const saveRot = useMutation({
    mutationFn: () =>
      upsertRotFn({
        data: {
          id: editingRot?.id,
          tenantId: tenantId!,
          name: rotForm.name.trim(),
          description: rotForm.description.trim() || null,
          ackWindowMinutes: rotForm.ackWindowMinutes,
          escalationMinutes: rotForm.escalationMinutes,
          isActive: rotForm.isActive,
        },
      }),
    onSuccess: () => {
      toast.success(editingRot ? "Rotation updated" : "Rotation created");
      setRotOpen(false);
      qc.invalidateQueries({ queryKey: ["on-call", tenantId] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed to save"),
  });

  const removeRot = useMutation({
    mutationFn: (id: string) => delRotFn({ data: { tenantId: tenantId!, id } }),
    onSuccess: () => {
      toast.success("Rotation removed");
      qc.invalidateQueries({ queryKey: ["on-call", tenantId] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  // Shift dialog
  const [shiftOpen, setShiftOpen] = useState(false);
  const [shiftRotId, setShiftRotId] = useState<string | null>(null);
  const [editingShift, setEditingShift] = useState<Shift | null>(null);
  const [shiftForm, setShiftForm] = useState({
    userId: "",
    role: "primary" as "primary" | "secondary" | "lead",
    startsAt: "",
    endsAt: "",
    contactEmail: "",
    contactPhone: "",
    notes: "",
  });

  const openNewShift = (rotationId: string) => {
    setShiftRotId(rotationId);
    setEditingShift(null);
    const now = new Date();
    const end = new Date(now.getTime() + 8 * 3600 * 1000);
    setShiftForm({
      userId: teammates[0]?.id ?? "",
      role: "primary",
      startsAt: toLocalInput(now),
      endsAt: toLocalInput(end),
      contactEmail: "",
      contactPhone: "",
      notes: "",
    });
    setShiftOpen(true);
  };

  const openEditShift = (rotationId: string, s: Shift) => {
    setShiftRotId(rotationId);
    setEditingShift(s);
    setShiftForm({
      userId: s.user_id,
      role: (s.role as any) ?? "primary",
      startsAt: toLocalInput(new Date(s.starts_at)),
      endsAt: toLocalInput(new Date(s.ends_at)),
      contactEmail: s.contact_email ?? "",
      contactPhone: s.contact_phone ?? "",
      notes: s.notes ?? "",
    });
    setShiftOpen(true);
  };

  const saveShift = useMutation({
    mutationFn: () =>
      upsertShiftFn({
        data: {
          id: editingShift?.id,
          tenantId: tenantId!,
          rotationId: shiftRotId!,
          userId: shiftForm.userId,
          role: shiftForm.role,
          startsAt: new Date(shiftForm.startsAt).toISOString(),
          endsAt: new Date(shiftForm.endsAt).toISOString(),
          contactEmail: shiftForm.contactEmail.trim() || null,
          contactPhone: shiftForm.contactPhone.trim() || null,
          notes: shiftForm.notes.trim() || null,
        },
      }),
    onSuccess: () => {
      toast.success(editingShift ? "Shift updated" : "Shift added");
      setShiftOpen(false);
      qc.invalidateQueries({ queryKey: ["on-call", tenantId] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed to save"),
  });

  const removeShift = useMutation({
    mutationFn: (id: string) => delShiftFn({ data: { tenantId: tenantId!, id } }),
    onSuccess: () => {
      toast.success("Shift removed");
      qc.invalidateQueries({ queryKey: ["on-call", tenantId] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const stats = useMemo(() => {
    const active = allShifts.filter((s) => shiftStatus(s) === "active").length;
    const upcoming = allShifts.filter((s) => shiftStatus(s) === "upcoming").length;
    const coverage = rotations.filter((r) =>
      (shiftsByRotation.get(r.id) ?? []).some((s) => shiftStatus(s) === "active"),
    ).length;
    return { active, upcoming, coverage };
  }, [allShifts, rotations, shiftsByRotation]);

  return (
    <div className="container mx-auto p-6 max-w-6xl space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <CalendarClock className="h-6 w-6" /> On-call rotations
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Define escalation pools and time-bounded shifts. The notification dispatcher resolves the on-call
            user from these shifts when incidents fire.
          </p>
        </div>
        <Button onClick={openNewRot}>
          <Plus className="h-4 w-4 mr-2" /> New rotation
        </Button>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Rotations" value={rotations.length} />
        <StatCard label="Covered now" value={`${stats.coverage} / ${rotations.length}`} tone={stats.coverage < rotations.length ? "warn" : "default"} />
        <StatCard label="Active shifts" value={stats.active} />
        <StatCard label="Upcoming" value={stats.upcoming} />
      </div>

      {rotQ.isLoading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading rotations…
        </div>
      ) : rotations.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <ShieldAlert className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">
              No rotations yet. Create one to start scheduling on-call coverage.
            </p>
            <Button className="mt-4" onClick={openNewRot}>
              <Plus className="h-4 w-4 mr-2" /> Create rotation
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {rotations.map((r) => {
            const shifts = shiftsByRotation.get(r.id) ?? [];
            const activeShift = shifts.find((s) => shiftStatus(s) === "active");
            const isOpen = expanded[r.id] ?? true;
            return (
              <Card key={r.id}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2 min-w-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 mt-0.5"
                        onClick={() => setExpanded((e) => ({ ...e, [r.id]: !isOpen }))}
                      >
                        {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </Button>
                      <div className="min-w-0">
                        <CardTitle className="text-base flex items-center gap-2">
                          {r.name}
                          {!r.is_active && <Badge variant="outline">Paused</Badge>}
                        </CardTitle>
                        <CardDescription className="mt-1">
                          {r.description || "No description"}
                        </CardDescription>
                        <div className="text-xs text-muted-foreground mt-2 flex flex-wrap gap-x-4 gap-y-1">
                          <span>Ack window: <span className="font-mono">{r.ack_window_minutes}m</span></span>
                          <span>Escalate after: <span className="font-mono">{r.escalation_minutes}m</span></span>
                          {activeShift ? (
                            <span className="text-success">
                              On call: {teammateLabel(activeShift.user_id)} ({activeShift.role})
                            </span>
                          ) : (
                            <span className="text-warning">No active shift</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button variant="outline" size="sm" onClick={() => openNewShift(r.id)}>
                        <Plus className="h-3.5 w-3.5 mr-1" /> Shift
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openEditRot(r)}>Edit</Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Remove rotation ${r.name}`}
                        onClick={() => {
                          if (confirm(`Remove "${r.name}" and all its shifts?`)) removeRot.mutate(r.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                {isOpen && (
                  <CardContent>
                    {shifts.length === 0 ? (
                      <div className="text-sm text-muted-foreground text-center py-6">
                        No shifts scheduled yet.
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Status</TableHead>
                            <TableHead>Teammate</TableHead>
                            <TableHead>Role</TableHead>
                            <TableHead>Window</TableHead>
                            <TableHead>Contact</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {shifts.map((s) => {
                            const status = shiftStatus(s);
                            return (
                              <TableRow key={s.id} className={status === "past" ? "opacity-60" : ""}>
                                <TableCell>
                                  {status === "active" && (
                                    <Badge className="bg-success/15 text-success border border-success/30">
                                      On call now
                                    </Badge>
                                  )}
                                  {status === "upcoming" && (
                                    <Badge variant="outline">
                                      In {formatDistanceToNow(new Date(s.starts_at))}
                                    </Badge>
                                  )}
                                  {status === "past" && (
                                    <Badge variant="outline">Ended</Badge>
                                  )}
                                </TableCell>
                                <TableCell className="font-medium">
                                  <div className="flex items-center gap-1.5">
                                    <Users className="h-3.5 w-3.5 text-muted-foreground" />
                                    {teammateLabel(s.user_id)}
                                  </div>
                                </TableCell>
                                <TableCell><Badge variant="outline" className="capitalize">{s.role}</Badge></TableCell>
                                <TableCell className="text-xs">
                                  <div>{format(new Date(s.starts_at), "MMM d, HH:mm")}</div>
                                  <div className="text-muted-foreground">→ {format(new Date(s.ends_at), "MMM d, HH:mm")}</div>
                                </TableCell>
                                <TableCell className="text-xs text-muted-foreground">
                                  {s.contact_email || s.contact_phone || "—"}
                                </TableCell>
                                <TableCell className="text-right space-x-1">
                                  <Button variant="ghost" size="sm" onClick={() => openEditShift(r.id, s)}>Edit</Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    aria-label="Remove shift"
                                    onClick={() => {
                                      if (confirm("Remove this shift?")) removeShift.mutate(s.id);
                                    }}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Rotation dialog */}
      <Dialog open={rotOpen} onOpenChange={setRotOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingRot ? "Edit rotation" : "New rotation"}</DialogTitle>
            <DialogDescription>
              Name an escalation pool and set ack / escalation windows used by the dispatcher.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={rotForm.name} onChange={(e) => setRotForm((f) => ({ ...f, name: e.target.value }))} placeholder="Ops primary" />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={rotForm.description} onChange={(e) => setRotForm((f) => ({ ...f, description: e.target.value }))} rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Ack window (min)</Label>
                <Input type="number" min={1} max={1440} value={rotForm.ackWindowMinutes}
                  onChange={(e) => setRotForm((f) => ({ ...f, ackWindowMinutes: parseInt(e.target.value || "0", 10) }))} />
              </div>
              <div className="space-y-2">
                <Label>Escalate after (min)</Label>
                <Input type="number" min={1} max={1440} value={rotForm.escalationMinutes}
                  onChange={(e) => setRotForm((f) => ({ ...f, escalationMinutes: parseInt(e.target.value || "0", 10) }))} />
              </div>
            </div>
            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <div className="text-sm font-medium">Active</div>
                <div className="text-xs text-muted-foreground">Disable to pause without deleting shifts.</div>
              </div>
              <Switch checked={rotForm.isActive} onCheckedChange={(v) => setRotForm((f) => ({ ...f, isActive: v }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRotOpen(false)}>Cancel</Button>
            <Button onClick={() => saveRot.mutate()} disabled={saveRot.isPending || !rotForm.name.trim()}>
              {saveRot.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingRot ? "Save changes" : "Create rotation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Shift dialog */}
      <Dialog open={shiftOpen} onOpenChange={setShiftOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingShift ? "Edit shift" : "New shift"}</DialogTitle>
            <DialogDescription>Assign a teammate to a time window.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Teammate</Label>
              {teammates.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  No teammates found in this tenant. Invite users from Settings first.
                </p>
              ) : (
                <Select value={shiftForm.userId} onValueChange={(v) => setShiftForm((f) => ({ ...f, userId: v }))}>
                  <SelectTrigger><SelectValue placeholder="Pick a teammate" /></SelectTrigger>
                  <SelectContent>
                    {teammates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name || t.id.slice(0, 8)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={shiftForm.role} onValueChange={(v) => setShiftForm((f) => ({ ...f, role: v as any }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="primary">Primary</SelectItem>
                    <SelectItem value="secondary">Secondary</SelectItem>
                    <SelectItem value="lead">Lead</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div />
              <div className="space-y-2">
                <Label>Starts</Label>
                <Input type="datetime-local" value={shiftForm.startsAt} onChange={(e) => setShiftForm((f) => ({ ...f, startsAt: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Ends</Label>
                <Input type="datetime-local" value={shiftForm.endsAt} onChange={(e) => setShiftForm((f) => ({ ...f, endsAt: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Contact email</Label>
                <Input value={shiftForm.contactEmail} onChange={(e) => setShiftForm((f) => ({ ...f, contactEmail: e.target.value }))} placeholder="optional" />
              </div>
              <div className="space-y-2">
                <Label>Contact phone</Label>
                <Input value={shiftForm.contactPhone} onChange={(e) => setShiftForm((f) => ({ ...f, contactPhone: e.target.value }))} placeholder="optional" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea rows={2} value={shiftForm.notes} onChange={(e) => setShiftForm((f) => ({ ...f, notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShiftOpen(false)}>Cancel</Button>
            <Button onClick={() => saveShift.mutate()} disabled={saveShift.isPending || !shiftForm.userId || !shiftForm.startsAt || !shiftForm.endsAt}>
              {saveShift.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingShift ? "Save changes" : "Add shift"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: number | string;
  tone?: "default" | "warn";
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className={"text-2xl font-semibold mt-1 " + (tone === "warn" ? "text-warning" : "")}>
          {value}
        </div>
      </CardContent>
    </Card>
  );
}

function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}